/**
 * Waiver section on the booking checkout step.
 *
 * Renders one `WaiverConsentCard` per active waiver and stores what the booker
 * draws/ticks in booking context. It deliberately does **not** submit: on the
 * guest path the account does not exist yet, so submission happens later, from
 * whichever step actually creates the booking.
 *
 * Renders nothing when the club has no waivers — the common case.
 *
 * @module pages/booking/components/WaiverSection
 */

import { WaiverConsentCard } from '@butter/ui/domains/legal';
import { useWaiverRequirements, downloadWaiverDocument } from '~/services/internalApi/waiver';
import { useBooking } from '~/services/internalApi/booking';
import { downloadBlob } from '@butter/ui/utils';
import { butterToast } from '@butter/ui/patterns';
import { RetryBanner } from '@butter/ui/components/feedback';
import { CheckoutSection } from './CheckoutSection';

interface WaiverSectionProps {
  clubId: number | null | undefined;
  disabled?: boolean;
  /** 1px rule above the heading. @default false */
  divider?: boolean;
}

export function WaiverSection({ clubId, disabled = false, divider = false }: WaiverSectionProps) {
  // `required`, not "all active" — a signed-in member who already cleared the
  // entry gate has nothing outstanding, and asking them again at checkout would
  // be the same waiver twice in one session.
  const { required: waivers, isLoading, hasAnswer, refetch } = useWaiverRequirements(clubId);
  const { waiverSignatures, waiverAgreements, setWaiverSignature, setWaiverAgreement } = useBooking();

  const handleDownload = async (waiverVersionId: number) => {
    if (!clubId) return;
    try {
      downloadBlob(await downloadWaiverDocument(clubId, waiverVersionId));
    } catch (error) {
      butterToast.error(error instanceof Error ? error.message : 'Failed to download waiver');
    }
  };

  // No skeleton: this section is absent for most clubs, and a placeholder that
  // usually resolves to nothing is worse than a section that appears once loaded.
  if (isLoading) return null;

  // An unreachable requirements list is a STATE, not nothing. The checkout gate
  // refuses on anything short of a loaded list, so rendering null here leaves the
  // visitor with a dead Confirm button and no way to act — including at clubs
  // that have no waiver at all, where the failure is unrelated to them.
  //
  // `!hasAnswer` rather than `isError` or `!isSuccess`: an offline query is
  // PAUSED (neither success nor error), and `isSuccess` also goes false on a
  // background refetch failure while the good data is still cached — which would
  // put this banner over a club whose waiver list we already have.
  if (!hasAnswer) {
    return (
      <CheckoutSection title="Waiver" divider={divider} data-testid="booking-waiver-section">
        <RetryBanner
          message="We couldn't load this club's waiver, so we can't complete your booking yet."
          onRetry={() => void refetch()}
          testId="waiver-load-failed"
        />
      </CheckoutSection>
    );
  }

  if (waivers.length === 0) return null;

  return (
    <CheckoutSection title="Waiver" divider={divider} data-testid="booking-waiver-section">
      {waivers.map((waiver) => (
        <WaiverConsentCard
          key={waiver.waiverVersionId}
          title={waiver.title}
          contentHtml={waiver.content}
          requireSignature={waiver.requireSignature}
          requireAgreement={waiver.requireAgreement}
          agreementText={waiver.agreementText}
          signature={waiverSignatures[waiver.waiverVersionId] ?? null}
          onSignatureChange={(value) => setWaiverSignature(waiver.waiverVersionId, value)}
          agreed={waiverAgreements[waiver.waiverVersionId] ?? false}
          onAgreedChange={(value) => setWaiverAgreement(waiver.waiverVersionId, value)}
          onDownloadPdf={() => handleDownload(waiver.waiverVersionId)}
          disabled={disabled}
          data-testid={`waiver-consent-${waiver.waiverVersionId}`}
        />
      ))}
    </CheckoutSection>
  );
}
